import { ApiSerializedError } from '../model/api';
import { TAppFunction } from '../services/i18n';
import {PayloadAction} from "@reduxjs/toolkit";
import type {ApiResponse} from '../generated-api';

export function getApiErrorData(action: any, t: TAppFunction) {
  let message = null;
  let code = null;

  // TODO: update based on KWS API error reposnses
  // Read message from possible json payload
  if ((action.payload as any)?.message) {
    message = (action.payload as any)?.message;
  } else if ('error' in action) {
    message = (action.error as ApiSerializedError)?.message;
  }

  if ((action.payload as any)?.code) {
    code = (action.payload as any)?.code;
  } else if ('error' in action) {
    code = (action.error as ApiSerializedError)?.code;
  }

  switch (message) {
    case 'Network request failed':
      message = t('Network request failed');
      break;
    case 'Timeout':
      message = t('API Timeout');
      break;
    case 'Rejected':
      message = t('Network error:') + `\n(${action.error}, ${JSON.stringify(action.payload)})`;
      break;
    default:
      switch (code?.[0]) {
        case '5':
          message = t('Server error');
          break;
        case '4':
          message = t('Network request failed');
          break;
      }
  }

  return { message, code };
}

export function getApiErrorMessage(action: any, t: TAppFunction) {
  return getApiErrorData(action, t).message;
}

export function isApiResultError(result: any) {
  return 'error' in result;
}

export const downloadFileAsBlob = async (response: PayloadAction<any>, defaultFileName: string) => {
  const apiResponse = response.payload; // raw response
  const blob = await apiResponse.value(); // blob data
  const contentDisposition = apiResponse.raw.headers.get('content-disposition');

  let filename;
  if (contentDisposition) {
    const filenameStarMatch = contentDisposition.match(/filename\*=UTF-8''(.+?)(?:;|$)/);
    if (filenameStarMatch) {
      filename = decodeURIComponent(filenameStarMatch[1]);
    } else {
      const filenameMatch = contentDisposition.match(/filename="?(.+?)"?(?:;|$)/);
      if (filenameMatch) {
        filename = decodeURIComponent(filenameMatch[1]);
      }
    }
  }

  const url = window.URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename || defaultFileName;
  document.body.appendChild(a);
  a.click();
  window.URL.revokeObjectURL(url);
  document.body.removeChild(a);
}

/**
 * Pagination metadata the backend sends as response headers from `paginate(...)`
 * (`app/controllers/concerns/pageable.rb`). `total` counts the whole scope,
 * `totalFiltered` only what the current filter matches -- interactions using
 * `with_pagination_meta` send both, plainer endpoints only the former.
 */
export interface PageInfo {
  total?: number;
  totalFiltered?: number;
  pageCount?: number;
  currentPage?: number;
  perPage?: number;
}

export interface PagedResult<T> extends PageInfo {
  items: T[];
}

function headerNumber(raw: Response, name: string): number | undefined {
  const value = raw.headers.get(name);
  if (value === null || value === '') {
    return undefined;
  }
  const parsed = Number(value);
  return isNaN(parsed) ? undefined : parsed;
}

export function getPageInfo(raw: Response): PageInfo {
  return {
    total: headerNumber(raw, 'Total-Records'),
    totalFiltered: headerNumber(raw, 'Total-Filtered-Records'),
    pageCount: headerNumber(raw, 'Page-Count'),
    currentPage: headerNumber(raw, 'Current-Page'),
    perPage: headerNumber(raw, 'Per-Page'),
  };
}

/** Turns a generated `...Raw()` call into items plus the pagination headers. */
export async function toPagedResult<T>(response: ApiResponse<Array<T>>): Promise<PagedResult<T>> {
  return {...getPageInfo(response.raw), items: await response.value()};
}

/** Number of records the current filter matches, if the endpoint reports it. */
export function getFilteredTotal(page: PageInfo): number | undefined {
  return page.totalFiltered !== undefined ? page.totalFiltered : page.total;
}
